import * as R from 'ramda';
import { createStructuredSelector } from 'reselect';
import { compose, withHandlers } from 'react-recompose';
import { connect, useDispatch, useSelector } from 'react-redux';
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
// features
import { AuthWrapper } from '../../../permission';
import PC from '../../../permission/role-permission';
import { getTabIdByIndex } from '../../../configurations/helpers';
import { makeSelectBranchesTree } from '../../../branch/selectors';
import { makeSelectAuthorities } from '../../../permission/selectors';
import { setActiveConfigGroupWithTab } from '../../../configurations/actions';
import { makeSelectActiveGroupWithTab } from '../../../configurations/selectors';
import { makeSelectPinnedBranches, makeSelectPinnedMenuItems } from '../../../profile/selectors';
// helpers/constants
import * as G from '../../../../helpers';
// icons
import * as I from '../../../../svgs';
// ui
import { Box, RelativeBox, AbsoluteBox } from '../../../../ui';
// feature navbar-menu
import * as H from '../../helpers';
import TopSection from './top-section';
import VersionRelease from './version-release';
import SidebarItemComponent from './sidebar-item';
import BranchesDropdown from './branches-dropdown';
import { HrDivider, LgMenuWrapper, SidebarSearchInput } from './ui';
import { toggleSubmenus, addPinnedItemToList } from '../../actions';
import { makeSelectSidebar, makeSelectSubmenusStatus } from '../../selectors';
import configurationTabs, { actionToGroupMap, groupToActionMap } from '../../configs/configuration-tabs';
//////////////////////////////////////////////////

const enhance = compose(
  withHandlers({
    handleClickPlusIcon: ({ addPinnedItemToList }) =>
      (item) => addPinnedItemToList(item.key),
  }),
);

const SidebarComponent = ({
  sidebar,
  styling,
  branches,
  branchTree,
  authorities,
  isSidebarOpen,
  subMenusStatus,
  pinnedBranches,
  toggleSubmenus,
  currentMenuItem,
  pinnedMenuItems,
  setSidebarStatus,
  handleOpenEntTree,
  handleSwitchBranch,
  handleClickNavItem,
  handleClickPlusIcon,
}) => {
  const sidebarRef = useRef(sidebar);

  const dispatch = useDispatch();

  const [typing, setTyping] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [submenusToggled, setSubmenusToggled] = useState(false);

  const [currentTab, setCurrentTab] = useState(R.prop('tab', G.parseQueryString()));

  const activeGroupWithTab = useSelector(makeSelectActiveGroupWithTab());

  useEffect(() => {
    const configTabs = R.propOr([], currentMenuItem, configurationTabs);
    const tabIndex = H.getIndexByTabId(currentTab, configTabs);

    dispatch(setActiveConfigGroupWithTab({
      tab: G.ifElse(R.lt(tabIndex, 0), 0, tabIndex),
      group: R.propOr(null, currentMenuItem, actionToGroupMap),
    }));
  }, []);

  useEffect(() => {
    const { tab, group } = activeGroupWithTab;

    if (G.isNilOrEmpty(tab)) return;

    const tabId = getTabIdByIndex(group, tab);

    const itemAction = R.prop(group, groupToActionMap);

    if (R.and(R.equals(tabId, currentTab), R.equals(itemAction, currentMenuItem))) return;

    setCurrentTab(tabId);
  }, [currentTab, currentMenuItem, activeGroupWithTab]);

  const stopTyping = useCallback(G.setDebounce(() => {
    setTyping(false);
    setSubmenusToggled(false);
  }, 200), []);

  const catchClickNavItem = useCallback((action) => {
    handleClickNavItem(action);

    dispatch(setActiveConfigGroupWithTab({
      tab: 0,
      group: R.propOr(null, action, actionToGroupMap),
    }));
  }, []);

  const handleSetSearchText = useCallback((event) => {
    const searchText = R.pathOr('', ['target', 'value'], event);

    setSearchText(searchText);

    if (R.lt(R.length(searchText), 2)) {
      return toggleSubmenus(R.map(() => false, subMenusStatus));
    }

    stopTyping();
    setTyping(true);
  }, []);

  const handleClearSearchText = useCallback(() => {
    setSearchText('');

    toggleSubmenus(R.map(() => false, subMenusStatus));
  }, []);

  const { filteredSideBar, subMenusExpanded } = useMemo(() => {
    if (typing) return sidebarRef.current;

    if (R.lt(R.length(searchText), 2)) {
      const data = {
        filteredSideBar: sidebar,
        subMenusExpanded: subMenusStatus,
      };

      sidebarRef.current = data;

      return data;
    }

    const criteria = R.toLower(searchText);

    const data = H.filterMenuItemsBySearchCriteria({
      sidebar,
      criteria,
      subMenusStatus,
      submenusToggled,
    });

    sidebarRef.current = data;

    return data;
  }, [typing, sidebar, searchText, subMenusStatus, submenusToggled]);

  const handleToggleSubmenus = useCallback((statuses) => {
    toggleSubmenus(statuses);

    setSubmenusToggled(true);
  }, []);

  const sidebarIconColor = R.pathOr(G.getTheme('colors.light.darkRed'), ['textColor'], styling);

  const branchDropdownProps = {
    branches,
    branchTree,
    pinnedBranches,
    handleOpenEntTree,
    handleSwitchBranch,
    iconsColor: sidebarIconColor,
  };

  const subItemsProps = {
    styling,
    currentTab,
    authorities,
    pinnedMenuItems,
    currentMenuItem,
    setSidebarStatus,
    handleClickPlusIcon,
    iconsColor: sidebarIconColor,
    subMenusStatus: subMenusExpanded,
    toggleSubmenus: handleToggleSubmenus,
    handleClickNavItem: catchClickNavItem,
  };

  const { glassEffect, backgroundColor } = styling;

  const bgColor = glassEffect ? H.hexToRgbA(R.or(backgroundColor, G.getTheme('colors.white')), 0.8) : backgroundColor;

  return (
    <LgMenuWrapper
      bg={bgColor}
      glassEffect={glassEffect}
      isMenuOpen={isSidebarOpen}
    >
      <TopSection />
      <AuthWrapper has={[PC.BRANCH_WRITE]}>
        <BranchesDropdown {...branchDropdownProps} />
      </AuthWrapper>
      <HrDivider m={8} />
      <RelativeBox>
        <SidebarSearchInput
          mx={15}
          bg={bgColor}
          maxLength={25}
          value={searchText}
          color={sidebarIconColor}
          onChange={handleSetSearchText}
          placeholder={G.getWindowLocale('titles:search', 'Search')}
        />
        { G.isNotEmpty(searchText) && (
          <AbsoluteBox top={5} right={20} cursor='pointer' onClick={handleClearSearchText}>
            {I.closeIcon(sidebarIconColor)}
          </AbsoluteBox>
        )}
      </RelativeBox>
      <Box overflowY='auto' height='calc(100vh - 200px)'>
        {
          G.isNotEmpty(filteredSideBar)
          && filteredSideBar.map(
            (item, i) => <SidebarItemComponent {...subItemsProps} key={i} item={item} />,
          )
        }
      </Box>
      <VersionRelease iconsColor={sidebarIconColor} />
    </LgMenuWrapper>
  );
};

const mapStateToProps = (state) => (createStructuredSelector({
  sidebar: makeSelectSidebar(state),
  authorities: makeSelectAuthorities(state),
  branchTree: makeSelectBranchesTree(state),
  subMenusStatus: makeSelectSubmenusStatus(state),
  pinnedBranches: makeSelectPinnedBranches(state),
  pinnedMenuItems: makeSelectPinnedMenuItems(state),
}));

export default connect(mapStateToProps, {
  toggleSubmenus,
  addPinnedItemToList,
})(enhance(SidebarComponent));
